<template>
  <div :class="{ 'ds-background-color-grey-100': withBackgroundColor }">
    <!-- Header Media: -->
    <DsSectionImage
      v-if="headerMedia"
      :image="headerMedia"
      :screen="screen"
      :theme-variant="headerMediaBackground ? headerMediaBackground : ''"
      class="ds-margin-bottom-48 ds-md-margin-bottom-96"
      is-contained
    />
    <section>
      <div
        class="ds-container"
        :class="[
          withoutPaddingTop ? 'ds-padding-bottom-48 ds-md-padding-bottom-96' : 'ds-padding-y-48 ds-md-padding-y-96'
        ]"
      >
        <!-- Title -->
        <div
          v-if="title"
          class="ds-grid ds-margin-bottom-24"
          :class="{ 'ds-md-margin-bottom-48': withTitleSizeLarge }"
        >
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--7">
            <DsHighlightedTitle
              :text="title"
              tag="h2"
              :size="{ small: 'medium', medium: 'xlarge', large: 'xlarge' }"
            />
          </div>
        </div>
        <div
          :class="[
            sideMedia && sideMedia.componentName === 'DsFigureElement'
              ? 'ds-align-items-flex-start'
              : 'ds-align-items-flex-end'
          ]"
          class="ds-grid"
        >
          <!-- Subtitle, Text, Text Footnote -->
          <div
            v-if="subtitle || text || footnote"
            class="ds-grid__col ds-grid__col--12 ds-md-grid__col--10 ds-lg-grid__col--6 ds-lg-grid__col--offset-1"
          >
            <p
              v-if="subtitle"
              class="ds-text ds-color-green ds-margin-bottom-24"
            >
              {{ subtitle }}
            </p>
            <RichText
              v-if="text"
              :data="text"
            >
              {{ text }}
            </RichText>
            <slot name="signatures" />
            <RichText
              v-if="footnote"
              :data="footnote"
              class="ds-text-small ds-color-grey-450 ds-margin-top-24"
            />
          </div>
          <div
            v-if="sideMedia && sideMedia?.componentName === 'DsFigureElement'"
            class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--4 ds-display-flex ds-lg-align-items-center ds-flex-direction-column ds-margin-top-48 ds-lg-margin-top-0"
          >
            <DsFigureElement
              class="ds-width-full"
              v-bind="sideMedia.content"
              caption-position="top"
            />
          </div>

          <div
            v-if="sideMedia && sideMedia?.componentName === 'DsImageElement'"
            class="ds-grid__col ds-grid__col--12 ds-display-flex ds-lg-align-items-center ds-flex-direction-column ds-margin-top-48 ds-lg-margin-top-0 ds-lg-grid__col--4 image"
          >
            <DsImageElement
              v-bind="sideMedia.content"
              caption-position="top"
            />
          </div>

          <p
            v-if="sideMedia && sideMedia?.content?.footnote"
            class="ds-text-small ds-color-grey-450 ds-lg-text-align-center ds-margin-top-24"
          >
            {{ sideMedia.content.footnote }}
          </p>
          <!--          </div>-->
        </div>
        <!-- Footer Media -->
        <div
          v-if="footerItems.length"
          class="ds-grid ds-margin-top-48 ds-md-margin-top-96"
        >
          <div
            v-for="item in footerItems"
            :key="`footer-media-item-${item.id}`"
            class="ds-grid__col ds-grid__col--6"
            :class="getFooterMediaItemClassName(item.componentName, item.id, footerItems.length)"
          >
            <component
              :is="item.component"
              v-bind="item.content"
            />
          </div>
        </div>
      </div>
    </section>
    <div
      v-if="withDivider"
      class="ds-container"
    >
      <DsDivider />
    </div>
  </div>
</template>

<script setup>
import { DsDivider, DsFigureElement, DsHighlightedTitle, DsSectionImage, DsLogoBar, DsImageElement } from 'awds'

const { screen } = useScreen()

const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  text: { type: Object, default: null },
  footnote: { type: Object, default: null },
  headerMedia: { type: Object, default: null },
  sideMedia: { type: Object, default: null },
  footerMediaItems: { type: Array, default: () => [] },
  withDivider: { type: Boolean, default: false },
  withBackgroundColor: { type: Boolean, default: false },
  headerMediaBackground: { type: String, default: '' },
  withTitleSizeLarge: { type: Boolean, default: false },
  withoutPaddingTop: { type: Boolean, default: false }
})

const getFooterMediaItemClassName = (componentName, index, total) => {
  let className = ''
  if (componentName === 'DsLogoBar' || componentName === 'DsImageElement') {
    className = 'ds-lg-grid__col--10 ds-lg-grid__col--offset-1'
  }
  if (componentName === 'DsFigureElement') {
    className += total > 1 && index === 0 ? 'ds-margin-bottom-48 ds-md-margin-bottom-0' : ''
    className += index === 0 ? ' ds-lg-grid__col--offset-1 ' : ''
    className += total > 1 ? ' ds-md-grid__col--6 ds-lg-grid__col--4' : 'ds-lg-grid__col--8'
  }
  return className
}

const getSideMediaItemClassName = (componentName) => {
  let className = ''
  if (componentName === 'DsLogoBar') {
    className = 'ds-md-grid__col--10 ds-lg-grid__col--2 ds-lg-grid__col--offset-2 ds-lg-grid__col--no-gutter'
  }
  if (componentName === 'DsImageElement') {
    className = 'ds-lg-grid__col--4'
  }
  if (componentName === 'DsFigureElement') {
    className = 'ds-lg-grid__col--4'
  }
  return className
}

const mediaItems = new Map([
  ['DsLogoBar', DsLogoBar],
  ['DsImageElement', DsImageElement],
  ['DsFigureElement', DsFigureElement]
])

const footerItems = computed(() => {
  return props.footerMediaItems.map((i, index) => ({
    ...i,
    id: index++,
    component: mediaItems.get(i.componentName)
  }))
})
</script>
